import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { useEditorStore } from './editor-store';
import { TooltipComponent } from 'src/common/tooltip/component';
import classNames, { Argument } from 'classnames';
import { BlockContent } from './types';
import { generateBlockId } from './helpers/generate-block-id';
import { useHover, useFloating, useInteractions, useDelayGroupContext, useDelayGroup, useId } from '@floating-ui/react';
import { offset, autoUpdate } from '@floating-ui/dom';
import { AddStrongIcon } from 'src/monet/icons';
import { getInsertionBoundary } from './helpers/block/get-insertion-boundary';
import { filterPossibleSectionBoundaries } from './layout-manager/filter-possible-section-boundaries';
import { useRequestAnimationFrame } from './utils/request-animation-frame.hook';
import { NeueTooltip } from './components/tooltip';
import { trackSegmentEvent } from 'src/utils/segment';
import { shallow } from 'zustand/shallow';

const CLICK_DELTA_THRESHOLD = 10;

type Props = {
  forceRefreshSectionBar: () => void;
};

export const NeueAddSectionButton = ({ forceRefreshSectionBar }: Props) => {
  const layout = useEditorStore((state) => state.layout, shallow);
  const outerAreaRect = useEditorStore((state) => state.outerAreaRect);
  const innerAreaRect = useEditorStore((state) => state.innerAreaRect);
  const canvasScrollPosition = useEditorStore((state) => state.canvasScrollPosition);
  const dispatchUserEditorAction = useEditorStore((state) => state.dispatchUserEditorAction);
  const clearSelectedBlock = useEditorStore((state) => state.clearSelectedBlock);
  const setCurrentSectionId = useEditorStore((state) => state.setCurrentSectionId);
  const setScrollToSectionId = useEditorStore((state) => state.setScrollToSectionId);
  const setIsDraggingSomethingOnEditor = useEditorStore((state) => state.setIsDraggingSomethingOnEditor);
  const setInsertionBoundary = useEditorStore((state) => state.setInsertionBoundary);

  const { boundaries, sectionGrids } = layout;

  const [dragging, setDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const [isOpen, setIsOpen] = useState(false);
  const { x, y, strategy, refs, context, update } = useFloating({
    middleware: [offset(16)],
    placement: 'right',
    strategy: 'fixed',
    open: isOpen,
    onOpenChange: setIsOpen,
  });
  const hover = useHover(context, {
    delay: { open: 500 },
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  useLayoutEffect(() => {
    if (isOpen && refs.reference.current && refs.floating.current) {
      return autoUpdate(refs.reference.current, refs.floating.current, update);
    }
  }, [isOpen, update, refs.floating, refs.reference]);

  const draggingActiveRef = useRef(false);
  const runOnRaf = useRequestAnimationFrame();

  const onStart = (e: DraggableEvent, args: DraggableData) => {
    draggingActiveRef.current = true;
    setIsDraggingSomethingOnEditor(true);
  };

  const onDrag = (e: DraggableEvent, args: DraggableData) => {
    if (!outerAreaRect || !innerAreaRect) return;
    const { node, x, y } = args;
    console.log('onDrag', x, y);
    const { clientX, clientY } = e as MouseEvent;
    const rect = node.getBoundingClientRect();
    runOnRaf(() => {
      if (draggingActiveRef.current) {
        clearSelectedBlock();
        setDragging(true);
        setPosition({ x, y });
        const boundary = getInsertionBoundary({
          boundaries: filterPossibleSectionBoundaries(boundaries, sectionGrids),
          elementRect: rect,
          overlapRect: outerAreaRect,
          x: clientX + canvasScrollPosition.x - innerAreaRect.x,
          y: clientY + canvasScrollPosition.y - innerAreaRect.y,
        });
        setInsertionBoundary(boundary || null);
      }
    });
  };

  const onStop = (e: DraggableEvent, args: DraggableData) => {
    draggingActiveRef.current = false;
    if (!outerAreaRect || !innerAreaRect) return;
    const { x, y } = args;
    if (dragging && (Math.abs(x) > CLICK_DELTA_THRESHOLD || Math.abs(y) > CLICK_DELTA_THRESHOLD)) {
      const { node } = args;
      const rect = node.getBoundingClientRect();
      const { clientX, clientY } = e as MouseEvent;
      const boundary = getInsertionBoundary({
        boundaries: filterPossibleSectionBoundaries(boundaries, sectionGrids),
        elementRect: rect,
        overlapRect: outerAreaRect,
        x: clientX + canvasScrollPosition.x - innerAreaRect.x,
        y: clientY + canvasScrollPosition.y - innerAreaRect.y,
      });
      if (boundary) {
        const id = generateBlockId();
        dispatchUserEditorAction({
          type: 'insert-section-at-boundary',
          id,
          boundary,
        });
        trackSegmentEvent('Editor Section Added', {
          dragged: true,
        });
        setCurrentSectionId(id);
        setScrollToSectionId(id);
      }
    } else {
      const id = generateBlockId();
      dispatchUserEditorAction({
        type: 'append-section',
        id,
      });
      trackSegmentEvent('Editor Section Added', {
        dragged: false,
      });
      setCurrentSectionId(id);
      setScrollToSectionId(id);
    }
    setDragging(false);
    setPosition({ x: 0, y: 0 });
    setIsDraggingSomethingOnEditor(false);
    setInsertionBoundary(null);
    forceRefreshSectionBar();
  };

  const renderAddButton = (dragVersion: boolean) => {
    let extraClassNames: Argument = '';
    if (dragVersion) {
      extraClassNames = [
        'w-10 h-10 pointer-events-auto',
        {
          'cursor-grabbing bg-neue-journey-bg': dragging,
          'cursor-pointer bg-transparent': !dragging,
        },
      ];
    } else {
      extraClassNames = [
        'bg-neue-journey-bg w-14 h-10 pointer-events-none',
        {
          'bg-neue-journey-bg group-hover:bg-neue-journey-bg-90': !dragging,
        },
      ];
    }
    return (
      <div
        className={classNames(
          'cc-drag-handle flex items-center justify-center rounded-lg transition-colors text-neue-journey-fg',
          extraClassNames
        )}
      >
        <AddStrongIcon />
      </div>
    );
  };

  return (
    <NeueTooltip placement='right' tooltipContent='Create new section' forceHide={dragging}>
      <div className='group relative z-neue-new-block-button-dragging cursor-pointer'>
        {renderAddButton(false)}
        <Draggable
          defaultClassName={classNames('absolute inset-0 opacity-0')}
          defaultClassNameDragging='cc-draggable-dragging w-10 h-10 left-2 top-0 transition-opacity opacity-100'
          position={dragging ? position : { x: 0, y: 0 }}
          onStart={onStart}
          onStop={onStop}
          onDrag={onDrag}
        >
          <div>{renderAddButton(true)}</div>
        </Draggable>
      </div>
    </NeueTooltip>
  );
};
